import { Grid, Stack, Typography, Box } from "@mui/material";

import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FormSelectInput } from "../../../components/form";

import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useValueSet } from "../../../hooks/useValueSet";
import { AvailableDays } from "./AvailableDays";
import { AvailableTime } from "./AvailableTime";
import { RequiredField } from "./RequiredField";
import { SubmitActions } from "./SubmitActions";
import { useFormatter } from "@/utils/useFormatter";
import { currentFeatures } from "@/configs/Settings";
import { Person } from "@mui/icons-material";
import { FormAutoComplete } from "@/components/form";

function cleanRepeatedList(list: any) {
  let cleanedList = new Set(list);
  return Array.from(cleanedList);
}

export function HealthCare({
  onNext,
  setCurrentStep,
  defaultValues,
  healthcareInfo,
}: any) {
  const { access } = useSessionContext();
  const { practitionerRole, userPractitionerRole } =
    usePractitionerRoleContext();

  const availablePractitioner = access?.roles?.includes("manager")
    ? practitionerRole?.map((el) => el.resource)
    : [userPractitionerRole];

  const availableSpecialites =
    availablePractitioner
      ?.map((el: any) => {
        const specialty = el?.specialty?.map(
          (e: fhir5.CodeableConcept) => e.coding?.[0]?.display,
        );
        return specialty;
      })
      .filter((array: any) => array !== undefined) || [];

  const cleanAvailableSpecialites = cleanRepeatedList(
    [].concat.apply([], availableSpecialites),
  );

  // Hook para buscar especialidades médicas usando useValueSet
  const { options: specialtiesOptions, loading: specialtiesLoading } =
    useValueSet({
      valueSet: "CBO", // ajuste conforme seu ValueSet

      customUrl: "https://fluxmed.cloud/fhir/r4/ValueSet/CBO-S", // ajuste se necessário
    });

  const { control, handleSubmit, register, unregister, setValue } = useForm({
    defaultValues,
  });

  const daysofweek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];
  const defaultDays = ["Seg", "Ter", "Qua", "Qui", "Sex"];

  let watchDaysChosen = useWatch({ name: "daysOfWeek", control });
  const selectedHealthService = useWatch({ name: "healthService", control });
  const selectedProfessionalActor = useWatch({
    name: "professionalActor",
    control,
  });

  const selectedDays = watchDaysChosen || defaultDays;

  function removeUnselectedDays(object: any, daysToRemove: any) {
    for (const day of daysToRemove) {
      if (object?.hasOwnProperty(day)) {
        delete object[day];
      }
    }
  }

  const onSubmit = async (data: any) => {
    const unselectedDays = daysofweek.filter(
      (el: any) => !selectedDays.includes(el),
    );

    removeUnselectedDays(data?.timeAvailable?.[0], unselectedDays);
    removeUnselectedDays(data?.timeAvailable?.[1], unselectedDays);

    await onNext({ healthcare: data });
  };

  const clickNext = async (data: any) => {
    await onSubmit(data);
    setCurrentStep((prev: number) => prev + 1);
  };

  // Processar especialidades baseado nas opções do ValueSet e especialidades disponíveis
  const specialties = (() => {
    if (specialtiesLoading) {
      return [{ value: "", display: "Carregando" }];
    }

    const auxList =
      specialtiesOptions
        ?.filter((option) => option?.display?.includes("Médico "))
        ?.map((item) => ({
          value: item.display,
          display:
            useFormatter.formatNameDisplay(
              item.display?.split("Médico ")?.[1],
            ) || item.display,
        }))
        ?.filter((el: any) => cleanAvailableSpecialites.includes(el.display)) ||
      [];

    const otherSpecialties =
      cleanAvailableSpecialites
        ?.filter((e: any) => !auxList?.some((el: any) => el?.display === e))
        ?.map((item: any) => ({
          display: item,
          value: item,
        })) || [];

    return [...auxList, ...otherSpecialties];
  })();

  // healthcareInfo vem de formData?.healthcare?.healthService no Form (string do serviço já salvo).
  // Não usar o objeto healthcare inteiro aqui: includes() precisa de string compatível com coding.display.
  const healthServiceForFilter =
    selectedHealthService ||
    (typeof healthcareInfo === "string"
      ? healthcareInfo
      : healthcareInfo?.healthService) ||
    "";

  const practitionerOptions = useMemo(() => {
    return (
      practitionerRole
        ?.filter((element: fhir5.BundleEntry<fhir5.PractitionerRole>) => {
          // Requisito base: precisa ter nome e estar ativo
          const hasDisplay = !!element?.resource?.practitioner?.display;
          const isActive = element?.resource?.active !== false;

          // fluxvet: não filtra por especialidade (a especialidade já está
          // vinculada ao perfil do profissional e não ao serviço selecionado)
          if (!currentFeatures.defaultApp) {
            return hasDisplay && isActive;
          }

          // fluxmed: filtra pelos profissionais cuja especialidade
          // corresponde ao serviço de saúde selecionado no formulário
          const auxSpecialty = element.resource?.specialty?.map(
            (e: fhir5.CodeableConcept) => e.coding?.[0]?.display,
          );
          return (
            hasDisplay &&
            isActive &&
            auxSpecialty?.includes(healthServiceForFilter)
          );
        })
        ?.map((el: fhir5.BundleEntry<fhir5.PractitionerRole>) => {
          return {
            label: useFormatter.formatNameDisplay(
              el.resource?.practitioner?.display || "",
            ),
            value: el.resource?.practitioner?.reference || "",
          };
        }) || []
    );
  }, [practitionerRole, healthServiceForFilter]);

  useEffect(() => {
    if (!access?.roles?.includes("manager")) return;
    if (!practitionerRole) return;
    if (!selectedProfessionalActor?.value) return;

    const isSelectedProfessionalAvailable = practitionerOptions.some(
      (option) => option.value === selectedProfessionalActor.value,
    );

    if (isSelectedProfessionalAvailable) return;

    setValue("professionalActor", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    access?.roles,
    practitionerRole,
    practitionerOptions,
    healthServiceForFilter,
    selectedProfessionalActor,
    setValue,
  ]);

  return (
    <Stack mb={2} width="100%" spacing={2}>
      <Typography
        variant="h6"
        fontWeight="600"
        textAlign="center"
        padding="16px 0px 16px 0px"
        color="neutral700.main"
      >
        Configure o tipo e horário do serviço de saúde associado à esta agenda.
      </Typography>

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        paddingBottom={2}
      >
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="600"
            mb={3}
            color="neutral600.main"
          >
            Serviço de saúde <RequiredField />
          </Typography>

          {!currentFeatures.defaultApp && (
            <Typography variant="body1" mb={2}>
              O serviço de saúde da agenda está associado a especialidade
              configurada no perfil profissional.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <FormSelectInput
            defaultValue={
              currentFeatures.defaultApp ? "" : specialties[0]?.value
            }
            name="healthService"
            label="Serviço de saúde"
            control={control}
            selectOptions={specialties}
            required
            autoFocus
          />
        </Grid>
      </Grid>
      {access?.roles?.includes("manager") && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          paddingBottom={2}
        >
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color="neutral600.main">
              Profissional associado a essa agenda
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormAutoComplete
              name="professionalActor"
              label="Digite o nome do profissional"
              control={control}
              selectOptions={practitionerOptions}
            />
          </Grid>
        </Grid>
      )}
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="600"
            mb={2}
            color="neutral600.main"
          >
            Dias e horários disponíveis na semana <RequiredField />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" mb={2}>
            Configure os horários em que este serviço ficará disponível durante
            a semana.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <AvailableDays
            selectedDays={selectedDays}
            control={control}
            register={register}
          />
        </Grid>
        <Grid item xs={12}>
          <AvailableTime
            selectedDays={selectedDays}
            control={control}
            defaultValues={defaultValues}
            unregister={unregister}
          />
        </Grid>
        <Grid item xs={12} mt={5}>
          <SubmitActions
            goBack={() => setCurrentStep((prev: number) => prev - 1)}
            goForward={handleSubmit(clickNext)}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
